import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/app/providers/AppContextProvider';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { filterInitialsInput, formatInitialsWithSpaces } from '@/services/utils';
import Navigation from '../Navigation';
import Title from '../Title';

interface StepProps {
	step: number;
	setStep: (step: number) => void;
	errors: Record<string, string | undefined>;
	isStepValid: boolean;
	isSaving: boolean;
	setIsSaving: (saving: boolean) => void;
}

const Step1 = ({ step, setStep, errors, isStepValid, isSaving, setIsSaving }: StepProps) => {
	const { t } = useTranslation();
	const initialsRef = useRef<HTMLInputElement | null>(null);
	const { applicationData, applicationManagement } = useAppContext();
	const [touched, setTouched] = useState<Record<string, boolean>>({});

	useEffect(() => {
		if (step === 1 && initialsRef.current && !applicationData.initials) {
			initialsRef.current.focus();
		}
	}, [step, applicationData.initials]);

	if (step !== 1) return null;

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;

		// For initials field, only allow letters and spaces
		if (name === 'initials') {
			const filteredValue = filterInitialsInput(value);
			applicationManagement.update({ [name]: filteredValue });
			return;
		}

		applicationManagement.update({ [name]: value });
	};

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setTouched((prev) => ({ ...prev, [name]: true }));

		// For initials field, add spaces between letters on blur
		if (name === 'initials') {
			const spacedValue = formatInitialsWithSpaces(value);
			applicationManagement.update({ [name]: spacedValue });
		}
	};

	const showError = (field: string) => touched[field] && errors?.[field];

	return (
		<div className="flex flex-col justify-center select-none">
			<div className="w-full md:w-8/12 mx-auto">
				<Title
					title={t('application.steps.step3.title')}
					subtitle={t('application.steps.step3.subtitle')}
				/>

				<div className="grid grid-cols-12 gap-3 mb-5 justify-center">
					<div className="col-span-4 md:col-span-2">
						<label className="block text-sm font-medium text-foreground mb-1">
							{t('application.steps.step3.initials')}
							<span className="text-destructive ml-1">*</span>
						</label>
						<Input
							ref={initialsRef}
							type="text"
							name="initials"
							autoComplete="given-name"
							value={applicationData.initials ?? ''}
							onChange={handleChange}
							onBlur={handleBlur}
							required
							maxLength={15}
							className={cn(showError('initials') && 'border-destructive')}
							data-testid="input-initials"
						/>
						{showError('initials') && (
							<p
								data-testid="input-feedback-initials"
								className="mt-1 text-sm text-destructive text-nowrap"
							>
								{errors.initials}
							</p>
						)}
					</div>
					<div className="col-span-8 md:col-span-2">
						<label className="block text-sm font-medium text-foreground mb-1">
							{t('application.steps.step3.prefix')}
						</label>
						<Input
							type="text"
							name="prefix"
							value={applicationData.prefix ?? ''}
							onChange={handleChange}
							maxLength={16}
							data-testid="input-prefix"
						/>
					</div>
					<div className="col-span-12 md:col-span-8">
						<label className="block text-sm font-medium text-foreground mb-1">
							{t('application.steps.step3.lastName')}
							<span className="text-destructive ml-1">*</span>
						</label>
						<Input
							type="text"
							name="lastName"
							autoComplete="family-name"
							value={applicationData.lastName ?? ''}
							onChange={handleChange}
							onBlur={handleBlur}
							required
							className={cn(showError('lastName') && 'border-destructive')}
							data-testid="input-lastName"
						/>
						{showError('lastName') && (
							<p data-testid="input-feedback-lastName" className="mt-1 text-sm text-destructive">
								{errors.lastName}
							</p>
						)}
					</div>
				</div>
			</div>

			<Navigation
				step={step}
				setStep={setStep}
				isStepValid={isStepValid}
				isSaving={isSaving}
				setIsSaving={setIsSaving}
			/>
		</div>
	);
};

export default Step1;
